interface PaypalOrderCaptureDetails {
  readonly create_time?: string
}

interface PaypalOrderActions {
  readonly order: {
    create: (input: {
      readonly application_context?: {
        readonly shipping_preference?: string
      }
      readonly purchase_units: Array<{
        readonly amount: {
          readonly value: string
        }
      }>
    }) => Promise<string>
    capture: () => Promise<PaypalOrderCaptureDetails>
  }
}

interface PaypalButtonsInstance {
  readonly close?: () => Promise<void> | void
  readonly render: (container: HTMLElement) => Promise<void>
}

interface PaypalNamespace {
  readonly Buttons: (options: {
    readonly createOrder: (
      data: unknown,
      actions: PaypalOrderActions,
    ) => Promise<string>
    readonly onApprove: (
      data: { readonly orderID: string },
      actions: PaypalOrderActions,
    ) => Promise<void>
    readonly onError?: (error: unknown) => void
    readonly style?: Record<string, boolean | number | string>
  }) => PaypalButtonsInstance
}

type PaypalWindow = Window &
  typeof globalThis & {
    paypal?: PaypalNamespace
    __hanaspeakPaypalSdkPromise?: Promise<PaypalNamespace>
  }

function buildPaypalSdkUrl(clientId: string) {
  const params = new URLSearchParams()
  params.set('client-id', clientId)
  params.set('components', 'buttons')
  params.set('currency', 'USD')
  params.set('intent', 'capture')

  return `https://www.paypal.com/sdk/js?${params.toString()}`
}

export async function loadPaypalSdk(clientId: string) {
  if (!clientId) {
    throw new Error('PayPal client ID is not configured.')
  }

  const paypalWindow = window as PaypalWindow

  if (paypalWindow.paypal) {
    return paypalWindow.paypal
  }

  if (paypalWindow.__hanaspeakPaypalSdkPromise) {
    return paypalWindow.__hanaspeakPaypalSdkPromise
  }

  paypalWindow.__hanaspeakPaypalSdkPromise = new Promise<PaypalNamespace>(
    (resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>(
        'script[data-hanaspeak-paypal-sdk="true"]',
      )

      const handleResolve = () => {
        if (paypalWindow.paypal) {
          resolve(paypalWindow.paypal)
          return
        }

        reject(new Error('PayPal SDK loaded without exposing window.paypal.'))
      }

      const handleError = () => {
        reject(new Error('PayPal SDK failed to load.'))
      }

      if (existingScript) {
        existingScript.addEventListener('load', handleResolve, { once: true })
        existingScript.addEventListener('error', handleError, { once: true })
        return
      }

      const script = document.createElement('script')
      script.async = true
      script.dataset.hanaspeakPaypalSdk = 'true'
      script.src = buildPaypalSdkUrl(clientId)
      script.addEventListener('load', handleResolve, { once: true })
      script.addEventListener('error', handleError, { once: true })
      document.head.appendChild(script)
    },
  )

  return paypalWindow.__hanaspeakPaypalSdkPromise
}
